/**
 * Date helpers for calendar handling.
 *
 * Events are stored as calendar dates (YYYY-MM-DD). The browser's Date API is
 * full of UTC traps that silently shift the calendar day:
 *   - `new Date('2026-06-22')` is parsed as UTC midnight.
 *   - `someDate.toISOString()` converts to UTC.
 * For any timezone west of UTC (e.g. US Central), both of these move the day by
 * one. These helpers keep everything anchored to the user's *local* wall-clock
 * date so an event entered for a given day is stored and shown on that day.
 */

/** Format a Date as YYYY-MM-DD using its LOCAL calendar fields (no UTC shift). */
export function toLocalDateString(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Format a Date as YYYY-MM-DDTHH:mm:ss using its LOCAL fields (no UTC shift).
 * Send this to the API so the event's wall-clock time is preserved as entered.
 */
export function toLocalDateTimeString(d: Date): string {
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  const ss = String(d.getSeconds()).padStart(2, '0')
  return `${toLocalDateString(d)}T${hh}:${mm}:${ss}`
}

/**
 * Parse a YYYY-MM-DD (or full ISO) string into a Date at LOCAL midnight.
 * Avoids the UTC-midnight interpretation that `new Date('YYYY-MM-DD')` applies.
 */
export function parseLocalDate(value: string): Date {
  const dateOnly = value.slice(0, 10)
  const [y, m, d] = dateOnly.split('-').map(Number)
  return new Date(y, (m || 1) - 1, d || 1)
}

/** True for events with no real time: "YYYY-MM-DD" or local midnight ("YYYY-MM-DDT00:00[:00]"). */
export function isDateOnlyEvent(eventDate?: string | null): boolean {
  return !!eventDate && /^\d{4}-\d{2}-\d{2}(T00:00(:00(\.0+)?)?)?$/.test(eventDate)
}

/** "Wednesday, January 6, 2027 at 6:30 PM", or just the date for date-only events. */
export function formatEventWhen(eventDate?: string | null): string {
  if (!eventDate) return 'No date set'
  const dateOnly = isDateOnlyEvent(eventDate)
  const d = dateOnly ? parseLocalDate(eventDate) : new Date(eventDate)
  return d.toLocaleString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    ...(dateOnly ? {} : { hour: 'numeric', minute: '2-digit' }),
  })
}
